



































































import { namespace } from 'vuex-class';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { CART, DETAIL, Mutations } from '@/store/types';
import { ConfigService } from '@/services/ConfigService';
import { UnitDefinition } from '@/services/ProductDeserializer';
import UnitSelector from '@/common/UnitSelector.vue';
import AmountSelector from '@/common/AmountSelector.vue';
import {PriceDto, ProductBasePrice, ProductDto, ProductNetFillQuantity} from '@eurogast/hub-client-typescript';
import { PriceDeserializer } from '@/services/PriceDeserializer';
import { NumberFormatter } from '@/services/NumberFormatter';
import { LockService } from '@/services/LockService';
import { ApiService } from '@/services/ApiService';
import ProductComponentBase from '@/common/ProductComponentBase';
import { Cart, CartItem } from '@/services/cart/model/cart';
import { CartEvents } from '@/services/cart/model/cart-events';
import { DependencyService } from '@/services/DependencyService';
import MatomoEvents from '@/matomo/events/MatomoEvents';
import AddToCartButton from '@/common/cart-button/AddToCartButton.vue';
import CartButtonMessage from '@/common/cart-button/CartButtonMessage.vue';

const cart = namespace(CART);
const detail = namespace(DETAIL);

@Component({ components: {CartButtonMessage, AddToCartButton, AmountSelector, UnitSelector } })
export default class DetailPurchaseSection extends ProductComponentBase {
  get units(): UnitDefinition[] {
    return ConfigService.parseUnits(this.localUnits);
  }

  get minSaleQuantity(): number {
    const minSalesQuantity = Number.parseFloat(this.localMinSaleQuantity);
    return minSalesQuantity > 0 ? minSalesQuantity : 1;
  }

  get cartItem(): CartItem {
    const unit = this.currentUnit;
    return CartItem.buildWithUnit(
        this.localSku,
        this.currentAmount,
        unit
    );
  }

  get bulkPrices(): PriceDto[] {
    if (this.localBulkPrices) {
      return PriceDeserializer.parsePriceList(this.localBulkPrices).sort((a, b) => a.minQuantity - b.minQuantity);
    }
    return null;
  }

  get basePrice(): ProductBasePrice {
    return null;
  }

  get netFillQuantity(): ProductNetFillQuantity {
    return null;
  }

  get sku(): string {
    return this.localSku;
  }

  get isPurchasable(): boolean {
    return this.availability === ProductDto.AvailabilityEnum.ACTIVE;
  }

  get showSelectors(): boolean {
    return this.isLoggedIn && this.isPurchasable;
  }


  @Prop({ required: true })
  public localSku: string;
  @Prop({ required: true })
  public localUnits: string;
  @Prop()
  public localBulkPrices: string;
  @Prop({ required: true })
  public localMinSaleQuantity: string;
  @Prop({ required: true })
  public availability: ProductDto.AvailabilityEnum;
  @Prop({ required: true })
  public showConvenienceMessage: boolean;

  @cart.State('cart')
  public cart: Cart;

  @detail.State('price')
  public price: PriceDto;

  public inCart = false;
  public unitHasChanged = false;
  public amountHasChanged = false;

  public currentAmount = this.minSaleQuantity;
  public showBulk = true;
  private lockService: LockService;
  private unitWatcher;
  private amountWatcher;
  private deletePending = false;
  private currentAmountString = Math.max(this.minSaleQuantity, 1).toString();

  private cartService = DependencyService.getCartService();

  @Watch('cart')
  public onCartChange() {
    if (!this.cart) {
      return;
    }

    if (this.unitWatcher) {
      this.unitWatcher();
    }
    if (this.amountWatcher) {
      this.amountWatcher();
    }

    const existingItem = this.cartService.getItem(this.localSku);
    if (existingItem) {
      this.currentAmount = existingItem.amount;
      this.currentUnit = this.units.find((unit) => unit.abbreviation === existingItem.unitAbbreviation);
      this.inCart = true; // set last to avoid change handlers saving the item
      this.unitWatcher = this.$watch('currentUnit', function(newValue, oldValue) {
        this.onUnitChange(newValue, oldValue);
      });
      this.amountWatcher = this.$watch('currentAmount', function(newValue, oldValue) {
        this.onAmountChange(newValue, oldValue);
      });
    } else {
      this.deletePending = false;
      this.inCart = false;
    }
  }

  @Watch('currentBulkPrice')
  public onBulkPriceChange() {
    this.$store.commit(Mutations.Detail.setPrice, this.currentBulkPrice);
  }

  public onAmountChange(newValue, oldValue) {
    this.putItemIfChanged(newValue, oldValue);
    if (!this.amountHasChanged && this.inCart) {
      this.amountHasChanged = true;
      setTimeout(() => {
        this.amountHasChanged = false;
      }, 3000);
    }
    this.emitAmountChange(this.currentAmount);
  }

  public onUnitChange(newValue, oldValue) {
    MatomoEvents.forDetailPage.unitChange.publishWithCartInformation(this.inCart);
    if (this.currentAmount * newValue.ratio < this.minSaleQuantity) {
      this.currentAmount = Math.ceil(this.minSaleQuantity / newValue.ratio);
      this.currentAmountString = this.currentAmount.toString();
      this.emitAmountChange(this.currentAmount);
    }
    this.putItemIfChanged(newValue, oldValue);
    if (!this.unitHasChanged && this.inCart) {
      this.unitHasChanged = true;
      setTimeout(() => {
        this.unitHasChanged = false;
      }, 3000);
    }
    this.emitUnitChange(newValue);
  }

  public addToCart() {
    MatomoEvents.forDetailPage.addItem.publish();
    if (!this.isValidAmount(this.currentAmountString)) {
      const minQuantity = Math.max(Math.ceil(this.minSaleQuantity), 1);
      ApiService.defaultInfoHandler(
        this.$t('productDetail.detailPurchaseSection.invalidAmount', { lowerBound: minQuantity }).toString()
      );
      return;
    }
    this.cartService.putItem(this.cartItem);
  }

  public removeFromCart() {
    if (this.inCart && !this.deletePending) {
      MatomoEvents.forDetailPage.removeItem.publish();
      this.deletePending = true;
      this.cartService.removeItem(this.cartItem);
    }
  }

  public mounted() {
    super.mounted();
    this.lockService = new LockService(this.beforeUnload.bind(this), 1000);
    this.currentAmount = Math.ceil(this.minSaleQuantity / this.currentUnit.ratio);
    this.currentAmountString = this.currentAmount.toString();
    this.$events.on(CartEvents.CART_UPDATE, this.onCartChange);
    this.onCartChange();
  }

  public quantityToString(minQuantity: number): string {
    return `${Math.max(minQuantity, 1)}`;
  }

  public beforeUnload() {
    this.cartService.putItem(this.cartItem, true);
  }

  public emitAmountChange(newValue) {
    this.$emit('amountChanged', newValue);
  }

  public emitUnitChange(newValue) {
    this.$emit('unitChanged', newValue);
  }

  private getPrice(price: number): string {
    return NumberFormatter.formatPrice(price);
  }

  private putItemIfChanged(newValue, oldValue) {
    if (newValue !== oldValue && this.inCart) {
      this.cartService.putItemWithLock(this.cartItem, this.lockService);
    }
  }

  private setCurrentAmountString(newValue) {
    MatomoEvents.forDetailPage.amountChange.publishWithCartInformation(this.inCart);
    this.currentAmountString = newValue;
    if (this.isValidAmount(newValue)) {
      this.currentAmount = newValue;
      this.emitAmountChange(newValue);
    }
  }
}
